import {cn} from '@tempmee/core'
import type {VariantProps} from 'class-variance-authority'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {LabelVariantsFactory} from './Variants'

export interface LabelProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof LabelVariantsFactory> {
  /**
   * Semantic color role.
   * @default 'info'
   */
  severity?: NonNullable<VariantProps<typeof LabelVariantsFactory>['severity']>
  /**
   * `filled` = solid color with white text; `standard` = faint tint with colored text.
   * @default 'standard'
   */
  variant?: NonNullable<VariantProps<typeof LabelVariantsFactory>['variant']>
  /** Optional leading icon, rendered in a 12×12 box. */
  icon?: ReactNode
  children: ReactNode
}

export const Label = forwardRef<HTMLSpanElement, LabelProps>(
  ({className, severity, variant, icon, children, ...props}, ref) => {
    // `gradient` + `standard` is the one severity whose text is itself the CTA
    // gradient (Figma). Clip the gradient to the glyphs via an inner span so the
    // chip keeps its faint background; every other pairing colors text flat.
    const isGradientText = severity === 'gradient' && variant !== 'filled'

    return (
      <span
        className={cn(LabelVariantsFactory({severity, variant}), className)}
        ref={ref}
        {...props}
      >
        {icon != null && (
          <span className="flex size-3 shrink-0 items-center justify-center [&_svg]:size-full">
            {icon}
          </span>
        )}
        {isGradientText ? (
          <span className="bg-gradient-cta bg-clip-text text-transparent">{children}</span>
        ) : (
          children
        )}
      </span>
    )
  },
)

Label.displayName = 'Label'
